<template>
  <!-- The row is a div so the two mark buttons are not buttons inside a button. The main button
       fills it; the marks sit on top at the right. -->
  <div class="group relative" :class="job.ignored_at ? 'bg-gray-50/70 dark:bg-transparent' : ''">
    <button type="button" @click="$emit('select', job.id)" :aria-current="selected ? 'true' : undefined"
      class="relative w-full text-left flex gap-2.5 px-3 py-2 transition-colors focus:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-gray-800/50"
      :class="selected ? 'bg-blue-50/60 dark:bg-blue-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'">
      <span class="absolute left-0 inset-y-0 w-0.5 transition-colors" :class="selected ? 'bg-blue-600 dark:bg-blue-400' : 'bg-transparent'" aria-hidden="true"></span>

      <JobAvatar :name="job.company_name" :src="job.company_logo_url" size="sm" class="mt-0.5 transition-opacity" :class="job.ignored_at ? 'opacity-50' : ''" />

      <div class="min-w-0 flex-1 transition-opacity" :class="job.ignored_at ? 'opacity-60' : ''">
        <h3 class="text-[13px] font-semibold leading-snug truncate pr-14"
          :class="selected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'">
          {{ job.title || 'Untitled job' }}
        </h3>

        <p class="text-xs text-gray-600 dark:text-gray-300 truncate">{{ subtitle }}</p>

        <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[11px] leading-4 text-gray-400 dark:text-gray-500">
          <span v-if="job.applied_at" title="You applied to this job"
            class="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 rounded-full bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
            <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
            Applied
          </span>
          <span v-if="hasAiResume" title="A resume was written for this job with AI"
            class="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 rounded-full text-white bg-gradient-to-r from-blue-500 via-violet-500 to-fuchsia-500">
            <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5Z"/></svg>
            AI resume
          </span>
          <span v-if="posted" class="inline-flex items-center gap-1">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>
            {{ posted }}
          </span>
          <span v-if="job.platform === 'stepstone'" title="Found on StepStone"
            class="px-1.5 rounded text-[10px] font-semibold bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300">StepStone</span>
          <span v-if="job.platform === 'manual'" title="You added this job yourself"
            class="px-1.5 rounded text-[10px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">Added by me</span>
          <span v-if="job.easy_apply"
            class="px-1.5 rounded text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">Easy Apply</span>
        </div>
      </div>
    </button>

    <!-- A mark that is on always shows. The other appears on hover or keyboard focus. Touch
         screens have no hover, so the same two are in the job's action bar. -->
    <div class="absolute top-1.5 right-2 flex items-center gap-0.5">
      <JobMarkButton kind="bookmark" :active="Boolean(job.bookmarked_at)" @click="$emit('toggle-bookmark', job)"
        :class="job.bookmarked_at ? '' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'" />
      <JobMarkButton kind="ignore" :active="Boolean(job.ignored_at)" @click="$emit('toggle-ignore', job)"
        :class="job.ignored_at ? '' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { postedAgo } from 'utils/timeAgo.js'
import JobAvatar from './JobAvatar.vue'
import JobMarkButton from './JobMarkButton.vue'
import { useResumeGenerationStore } from 'stores/resumeGeneration.js'

const props = defineProps({
  job: { type: Object, required: true },
  selected: { type: Boolean, default: false },
})
defineEmits(['select', 'toggle-bookmark', 'toggle-ignore'])

// A resume AI wrote exists for this job: the server says so, and a resume written just now (this visit) counts at once, without a reload.
const generation = useResumeGenerationStore()
const hasAiResume = computed(() => props.job.has_ai_resume === true || generation.byJob[props.job.id]?.status === 'succeeded')

// Counted from the date the job was posted to now. The text LinkedIn gave is only its age when it was fetched, so it is the fallback.
const posted = computed(() => postedAgo(props.job.posted_at) || props.job.posted_text)
const subtitle = computed(() => [props.job.company_name, props.job.location].filter(Boolean).join(' · '))
</script>
